import { useActivation } from '@context/MobileOne';
import {
  ButtonCheckbox,
  CardInfobox,
  Link,
  NavigationPageTitle,
  Ta11yText,
  CMSLink,
  ButtonPrimary,
  CardSupport,
  Notification,
  ScrollToTop,
  ButtonCancelActivation
} from '@core/index';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { tA11y } from '@utils/a11y/a11yHelpers';
import { useA11y } from '@context/Utils/A11y';
import {
  appAlert,
  appButtonType,
  appCardStyle,
  appCheckboxSize,
  appIconsPath,
  appInfoCardSize,
  appLinkSize,
  appLinkStyle
} from '@utils/globalConstant';
import { useEffect, useState } from 'react';
import Icons from '@core/Utils/Icons/Icons';

export function OverviewConsent() {
  // Context
  const { tA11yTranslate } = useA11y();
  const {
    getVviDocuments,
    vviDocuments,
    setStep8SubStep,
    setShowActivationHeader,
    isLoading,
    overviewFormSubmit,
    overviewForm,
    loginPasswordForm,
    personalDataForm,
    tariffActivationForm,
    phoneNumberActivationForm
  } = useActivation();

  // const
  const overviewHeader2Content = tA11y('ek_reg_overview_hdl2');
  const overviewHeader3Content = tA11y('ek_reg_overview_hdl3');
  const overviewHeader4Content = tA11y('ek_reg_overview_hdl4');
  const checkboxLabel1Content = tA11y('ek_reg_overview_chckbx_txt1');
  const checkboxLabel2Content = tA11y('ek_reg_overview_chckbx_txt2');
  const overviewbtnContent = tA11y('ek_reg_overview_btn1');
  const checkBoxError = tA11y('ek_reg_overview_chckbx_err1');

  // State
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [error, setError] = useState(null);

  // hooks
  useEffect(() => {
    getVviDocuments();
  }, []);

  const getDocLinkData = (base64) => {
    const base64Length = base64.length;
    // eslint-disable-next-line no-nested-ternary
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    const originalSizeInBytes = (base64Length * 3) / 4 - padding;
    const originalSizeInKB = originalSizeInBytes / 1024;
    return {
      url: `data:application/pdf;base64,${base64}`,
      size: originalSizeInKB.toFixed(2)
    };
  };

  const handleContinue = () => {
    // BFSG: remove console.log
    // console.log('check1', check1);
    // console.log('check2', check2);

    if (!check1) {
      setError(true);
      document.getElementById('termsAndConditions').focus();
    } else {
      setError(false);
      setShowActivationHeader(false);
      overviewFormSubmit({
        ...overviewForm,
        ...loginPasswordForm,
        ...personalDataForm,
        ...tariffActivationForm,
        ...phoneNumberActivationForm,
        termsAndConditions: true,
        marketingMultibrand: check2
      });
    }
  };

  const getDocName = (name) => tA11yTranslate(`ek_vv1_${name}`).content;

  return (
    <>
      <ScrollToTop>
        <section className="min-dvh-100 overview-consent-container">
          <AppPageContentWrapper>
            <div className="w-100 pb-40">
              <NavigationPageTitle title="ek_reg_overview_hdl1" variant="sm" />
            </div>
            <div className="details-container pb-20">
              <div className="title-container d-flex align-items-center text-primary">
                <Icons name='DOCUMENT' colorType='dark' width={24} height={24}/> 
                <Ta11yText
                  tag="h3"
                  textContent={overviewHeader2Content}
                  className="ek-h3 m-0 px-3"
                />
              </div>
            </div>
            <div className="w-100 pb-40">
              <CardInfobox
                cardStyle={appCardStyle.WARNING}
                cardSize={appInfoCardSize.LARGE}
                cardIcon={appIconsPath.DOWNLOAD}
                text="ek_reg_overview_vvi_info_txt1"
              />
            </div>
            <div className="w-100 link-container text-primary">
              {vviDocuments &&
                Object.keys(vviDocuments).length > 0 &&
                Object.entries(vviDocuments)?.map((doc) =>
                  doc[1] ? (
                    <div className="w-100" key={doc[0]}>
                      <Link
                        label={`${getDocName(doc[0])} (PDF, ${getDocLinkData(doc[1]).size} KB)`}
                        iconLeft={appIconsPath.DOWNLOAD}
                        linkStyle={`${appLinkStyle.PRIMARY} mb-16`}
                        linkSize={appLinkSize.LARGE}
                        href={getDocLinkData(doc[1]).url}
                        download={`${getDocName(doc[0])}.pdf`}
                      />
                    </div>
                  ) : null
                )}
            </div>
            <span className="w-100 border-bottom border-secondary d-flex my-40" />
            <div className="details-container pb-20">
              <div className="title-container d-flex align-items-center text-primary">
                <Ta11yText tag="h3" textContent={overviewHeader3Content} className="ek-h3 m-0" />
              </div>
            </div>
            <div className="w-100 text-primary pb-16">
              <ButtonCheckbox
                id="termsAndConditions"
                checkboxContent={checkboxLabel1Content}
                checkboxSize={appCheckboxSize.DEFAULT}
                value={check1}
                onChange={(val) => {
                  setError(false);
                  setCheck1(val);
                }}
                invalid={error}
                aria-describedby="checkbox-error"
              />
              <div className="px-20">
                <CMSLink
                  className="px-10"
                  iconRight={appIconsPath.ARROW_RIGHT_SMALL}
                  linkKey="ek_reg_overview_lnk1"
                />
              </div>
              {error ? (
                <div className="pt-20 ps-32" id='checkbox-error'>
                  <Notification type={appAlert.ERROR} message={checkBoxError?.content} />
                </div>
              ) : null}
            </div>
            <div className="details-container pb-8">
              <div className="title-container d-flex align-items-center text-primary">
                <Ta11yText
                  tag="p"
                  textContent={overviewHeader4Content}
                  className="ek-copy-sm-14-semibold m-0"
                />
              </div>
            </div>
            <div className="w-100 text-primary">
              <ButtonCheckbox
                id='marketingMultibrand'
                checkboxContent={checkboxLabel2Content}
                checkboxSize={appCheckboxSize.DEFAULT}
                value={check2}
                onChange={setCheck2}
              />
              <div className="px-20">
                <Link
                  label="ek_reg_overview_lnk2"
                  linkStyle={`${appLinkStyle.PRIMARY} px-10 py-8 w-100`}
                  linkSize={appLinkSize.DEFAULT}
                  onClick={() => {
                    setStep8SubStep(2);
                    setShowActivationHeader(false);
                  }}
                  iconRight={appIconsPath.ARROW_RIGHT_SMALL}
                />
                <CMSLink
                  className="px-10 pb-8 w-100"
                  iconRight={appIconsPath.ARROW_RIGHT_SMALL}
                  linkKey="ek_reg_overview_lnk3"
                />
                <CMSLink
                  className="px-10 pb-8 w-100"
                  iconRight={appIconsPath.ARROW_RIGHT_SMALL}
                  linkKey="ek_reg_overview_lnk4"
                />
              </div>
            </div>
            <div className="w-100 d-flex justify-content-center py-40">
              <div className="col-xxl-5 col-lg-5 col-10 px-0">
                <ButtonPrimary
                  buttonType={appButtonType.PRIMARY}
                  buttonContent={overviewbtnContent}
                  type="submit"
                  onClick={handleContinue}
                  isLoading={isLoading}
                />
              </div>
            </div>
            <CardSupport />
            <div className="d-flex justify-content-center mt-32">
              <ButtonCancelActivation />
            </div>
          </AppPageContentWrapper>
        </section>
      </ScrollToTop>
    </>
  );
}

export default OverviewConsent;
